import { ArrowLeft, Banknote, CalendarCheck, ClipboardCheck, ExternalLink, FileText, Upload, Users } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingScreen } from "./auth";
import { EmptyState, Metric, WorkspaceHeader } from "./common";
import {
  api,
  assignmentStatusLabel,
  formatMoney,
  isPapiAssignment,
  orderStatusLabel,
  paymentStatusLabel,
  type AdminData,
  type AssessmentAssignment,
  type AssessmentTemplate,
  type Order,
} from "./core";

export function AdminPortal() {
  const [data, setData] = useState<AdminData | null>(null);

  const load = async () => {
    const [summary, clients, orders, templates, submissions, reports] = await Promise.all([
      api<Record<string, number>>("/api/admin/summary"),
      api<{ clients: AdminData["clients"] }>("/api/admin/clients").then(result => result.clients),
      api<{ orders: Order[] }>("/api/admin/orders").then(result => result.orders),
      api<{ templates: AssessmentTemplate[] }>("/api/admin/assessment-templates").then(result => result.templates),
      api<{ submissions: AssessmentAssignment[] }>("/api/admin/submissions").then(result => result.submissions),
      api<{ reports: AdminData["reports"] }>("/api/admin/reports").then(result => result.reports),
    ]);
    setData({ summary, clients, orders, templates, submissions, reports });
  };

  useEffect(() => {
    load().catch(error => toast.error(error.message));
  }, []);

  if (!data) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-secondary/30">
      <WorkspaceHeader title="Admin MotherSense" description="Verifikasi pembayaran, assign asesmen, dan rilis laporan." />
      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-5">
          <Metric icon={Users} label="Klien" value={data.summary.clients ?? 0} />
          <Metric icon={CalendarCheck} label="Pesanan" value={data.summary.orders ?? 0} />
          <Metric icon={Banknote} label="Review bayar" value={data.summary.paymentReview ?? 0} />
          <Metric icon={ClipboardCheck} label="Submit tes" value={data.summary.submittedAssessments ?? 0} />
          <Metric icon={FileText} label="Laporan rilis" value={data.summary.releasedReports ?? 0} />
        </div>

        <AdminOrders data={data} onReload={load} />
        <div className="grid gap-6 xl:grid-cols-2">
          <AssignAssessment data={data} onReload={load} />
          <UploadReport data={data} onReload={load} />
        </div>
        <AdminSubmissions submissions={data.submissions} />
        <AdminClients clients={data.clients} />
      </main>
    </div>
  );
}

function AdminOrders({ data, onReload }: { data: AdminData; onReload: () => Promise<void> }) {
  const verify = async (paymentId?: string) => {
    if (!paymentId) return;
    try {
      await api(`/api/admin/payments/${paymentId}/verify`, { method: "POST" });
      toast.success("Pembayaran diverifikasi");
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal verifikasi");
    }
  };

  const reject = async (paymentId?: string) => {
    if (!paymentId) return;
    const reason = window.prompt("Alasan penolakan bukti transfer");
    if (!reason) return;
    try {
      await api(`/api/admin/payments/${paymentId}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
      toast.success("Pembayaran ditolak");
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal menolak");
    }
  };

  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Pesanan dan pembayaran</CardTitle>
        <CardDescription>Verifikasi bukti transfer dan pantau status pesanan klien.</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Klien</th>
              <th>Layanan</th>
              <th>Status</th>
              <th>Pembayaran</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map(order => (
              <tr key={order.id}>
                <td>{order.client?.name ?? "-"}</td>
                <td>
                  {order.service?.title}
                  <span className="block text-xs text-muted-foreground">{formatMoney.format(order.total)}</span>
                </td>
                <td>
                  <Badge tone={order.status === "confirmed" || order.status === "completed" ? "green" : "amber"}>
                    {orderStatusLabel[order.status]}
                  </Badge>
                </td>
                <td>
                  {order.payment ? (
                    <div className="grid gap-2">
                      <span>{paymentStatusLabel[order.payment.status]}</span>
                      {order.payment.proofDataUrl && (
                        <Button asChild size="sm" variant="outline">
                          <a href={order.payment.proofDataUrl} target="_blank" rel="noreferrer">
                            <ExternalLink />
                            Lihat bukti
                          </a>
                        </Button>
                      )}
                      {order.payment.proofFileName && (
                        <span className="text-xs text-muted-foreground">{order.payment.proofFileName}</span>
                      )}
                    </div>
                  ) : (
                    "Belum ada"
                  )}
                </td>
                <td>
                  {order.payment?.status === "review" ? (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => verify(order.payment?.id)}>
                        Verifikasi
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(order.payment?.id)}>
                        Tolak
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Tidak ada aksi</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function AssignAssessment({ data, onReload }: { data: AdminData; onReload: () => Promise<void> }) {
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const payload = {
      userId: String(form.get("userId") ?? ""),
      templateId: String(form.get("templateId") ?? ""),
      orderId: String(form.get("orderId") ?? "") || undefined,
      dueDate: String(form.get("dueDate") ?? "") || undefined,
    };
    try {
      await api("/api/admin/assessments/assign", { method: "POST", body: JSON.stringify(payload) });
      toast.success("Asesmen di-assign");
      formElement.reset();
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal assign asesmen");
    }
  };

  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Assign asesmen</CardTitle>
        <CardDescription>Kirim template tes ke portal klien.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="assign-user">Klien</Label>
            <select id="assign-user" name="userId" className="field-select" required>
              <option value="">Pilih klien</option>
              {data.clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="templateId">Template</Label>
            <select id="templateId" name="templateId" className="field-select" required>
              <option value="">Pilih template</option>
              {data.templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="orderId">Pesanan terkait</Label>
            <select id="orderId" name="orderId" className="field-select">
              <option value="">Tanpa pesanan</option>
              {data.orders.map(order => (
                <option key={order.id} value={order.id}>
                  {order.client?.name} - {order.service?.title}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dueDate">Batas isi</Label>
            <Input id="dueDate" type="date" name="dueDate" />
          </div>
          <Button type="submit">
            <ClipboardCheck />
            Assign
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function UploadReport({ data, onReload }: { data: AdminData; onReload: () => Promise<void> }) {
  const uploadableSubmissions = data.submissions.filter(item => !isPapiAssignment(item));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await api("/api/admin/reports", { method: "POST", body: form });
      toast.success("Laporan diunggah");
      formElement.reset();
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal unggah laporan");
    }
  };

  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Upload laporan PDF</CardTitle>
        <CardDescription>Laporan hanya terlihat klien setelah status rilis.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="report-user">Klien</Label>
            <select id="report-user" name="userId" className="field-select" required>
              <option value="">Pilih klien</option>
              {data.clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="report-title">Judul laporan</Label>
            <Input id="report-title" name="title" required placeholder="Laporan Tes Kesiapan Sekolah" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="report-assignment">Submission terkait</Label>
            <select id="report-assignment" name="assignmentId" className="field-select">
              <option value="">Tanpa submission</option>
              {uploadableSubmissions.map(item => (
                <option key={item.id} value={item.id}>
                  {item.client?.name} - {item.template?.title}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="report-order">Pesanan terkait</Label>
            <select id="report-order" name="orderId" className="field-select">
              <option value="">Tanpa pesanan</option>
              {data.orders.map(order => (
                <option key={order.id} value={order.id}>
                  {order.client?.name} - {order.service?.title}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="file">File PDF</Label>
            <Input id="file" type="file" name="file" accept="application/pdf" required />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="release" value="true" />
            Rilis langsung ke portal klien
          </label>
          <Button type="submit">
            <Upload />
            Unggah laporan
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function AdminSubmissions({ submissions }: { submissions: AssessmentAssignment[] }) {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Submission asesmen</CardTitle>
        <CardDescription>Jawaban online untuk bahan review psikolog.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {submissions.length === 0 && <EmptyState icon={ClipboardCheck} title="Belum ada submission" />}
        {submissions.map(item => (
          <div key={item.id} className="rounded-lg border p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-primary">{item.template?.title}</h3>
                  {isPapiAssignment(item) && <Badge>PAPI</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{item.response?.respondentProfile?.fullName ?? item.client?.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={item.status === "reviewed" ? "green" : "amber"}>{assignmentStatusLabel[item.status]}</Badge>
                <Button asChild size="sm" variant="outline">
                  <Link to={`/admin/submissions/${item.id}`}>Tinjau</Link>
                </Button>
              </div>
            </div>
            {isPapiAssignment(item) ? (
              <p className="mt-4 text-sm text-muted-foreground">
                {item.response?.scoringSnapshot
                  ? `Skor internal siap ditinjau. Total Atas ${item.response.scoringSnapshot.totalAtas}, Total Bawah ${item.response.scoringSnapshot.totalBawah}.`
                  : "Submission PAPI siap ditinjau psikolog."}
              </p>
            ) : (
              <div className="mt-4 grid gap-2 text-sm">
                {(item.questions ?? []).map(question => (
                  <div key={question.id} className="rounded-md bg-secondary/60 p-3">
                    <span className="font-medium text-primary">{question.prompt}</span>
                    <p className="mt-1 text-muted-foreground">
                      {item.response?.answers.find(answer => answer.questionId === question.id)?.value ?? "-"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function AdminSubmissionDetailPage() {
  const { id } = useParams();
  const [submission, setSubmission] = useState<AssessmentAssignment | null>(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const result = await api<{ submission: AssessmentAssignment }>(`/api/admin/submissions/${id}`);
    setSubmission(result.submission);
    setNotes(result.submission.review?.notes ?? "");
  };

  useEffect(() => {
    load().catch(error => toast.error(error.message));
  }, [id]);

  if (!submission) return <LoadingScreen />;

  const scoring = submission.response?.scoringSnapshot;
  const respondent = submission.response?.respondentProfile;

  const saveReview = async () => {
    try {
      await api(`/api/admin/submissions/${submission.id}/review`, {
        method: "POST",
        body: JSON.stringify({ notes }),
      });
      toast.success("Catatan review disimpan");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal menyimpan review");
    }
  };

  const generateReport = async () => {
    try {
      await api(`/api/admin/submissions/${submission.id}/generate-report`, { method: "POST" });
      toast.success("Draft laporan PAPI dibuat");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal membuat draft laporan");
    }
  };

  const releaseReport = async () => {
    if (!submission.report?.id) return;
    try {
      await api(`/api/admin/reports/${submission.report.id}/release`, { method: "POST" });
      toast.success("Laporan dirilis");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal merilis laporan");
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <WorkspaceHeader
        title={submission.template?.title ?? "Review Submission"}
        description="Tinjau jawaban, simpan catatan, buat draft laporan, lalu rilis saat siap."
      />
      <main className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to="/admin">
              <ArrowLeft />
              Kembali ke admin
            </Link>
          </Button>
          {isPapiAssignment(submission) && (
            <>
              <Button onClick={saveReview} variant="outline">
                Simpan catatan
              </Button>
              <Button onClick={generateReport}>Buat draft laporan</Button>
              {submission.report?.status === "draft" && (
                <Button onClick={releaseReport} variant="outline">
                  Rilis laporan
                </Button>
              )}
            </>
          )}
        </div>

        {respondent && (
          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="font-display text-2xl text-primary">Identitas peserta</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <p>
                <span className="font-medium text-primary">Nama:</span> {respondent.fullName}
              </p>
              <p>
                <span className="font-medium text-primary">Tanggal lahir:</span> {respondent.birthDate}
              </p>
              <p>
                <span className="font-medium text-primary">Usia:</span> {respondent.ageYears} tahun {respondent.ageMonths} bulan
              </p>
              <p>
                <span className="font-medium text-primary">Gender:</span> {respondent.gender ?? "-"}
              </p>
              <p>
                <span className="font-medium text-primary">Pendidikan:</span> {respondent.education}
              </p>
              <p>
                <span className="font-medium text-primary">Posisi:</span> {respondent.position}
              </p>
              <p>
                <span className="font-medium text-primary">Industri:</span>{" "}
                {respondent.industryOther ? `${respondent.industry} - ${respondent.industryOther}` : respondent.industry}
              </p>
              <p>
                <span className="font-medium text-primary">Masa kerja:</span> {respondent.tenureMonths} bulan
              </p>
            </CardContent>
          </Card>
        )}

        {scoring ? (
          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="font-display text-2xl text-primary">Skor PAPI</CardTitle>
              <CardDescription>
                Total Atas {scoring.totalAtas}, Total Bawah {scoring.totalBawah}
                {scoring.validationFlags.length ? `, validasi: ${scoring.validationFlags.join(", ")}` : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5">
              {scoring.aspects.map(aspect => (
                <div key={aspect.aspect} className="rounded-lg border p-4">
                  <h3 className="font-semibold text-primary">{aspect.aspect}</h3>
                  <div className="mt-3 grid gap-3">
                    {aspect.factors.map(factor => (
                      <div key={factor.code} className="rounded-md bg-secondary/60 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-primary">
                            {factor.code} - {factor.label}
                          </span>
                          <Badge>{factor.score}</Badge>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">{factor.interpretation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-lg">
            <CardContent className="pt-6">
              <EmptyState icon={ClipboardCheck} title="Skor internal belum tersedia" />
            </CardContent>
          </Card>
        )}

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-primary">Catatan psikolog</CardTitle>
            <CardDescription>
              {submission.report
                ? `Status laporan: ${submission.report.status === "released" ? "Sudah dirilis" : "Draft siap dirilis"}`
                : "Belum ada draft laporan untuk submission ini."}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Textarea className="min-h-36" value={notes} onChange={event => setNotes(event.target.value)} />
            {submission.report && (
              <div className="rounded-md bg-secondary/60 p-3 text-sm text-muted-foreground">
                <p className="font-medium text-primary">{submission.report.title}</p>
                <p>{submission.report.fileName}</p>
                {submission.report.status === "released" && (
                  <a className="mt-2 inline-flex text-primary underline" href={`/api/reports/${submission.report.id}/download`}>
                    Unduh laporan
                  </a>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function AdminClients({ clients }: { clients: AdminData["clients"] }) {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Klien</CardTitle>
        <CardDescription>Data kontak dan profil anak.</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nama</th>
              <th>Email</th>
              <th>Kota</th>
              <th>Anak</th>
              <th>Kebutuhan</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => (
              <tr key={client.id}>
                <td>{client.name}</td>
                <td>{client.email}</td>
                <td>{client.profile?.city ?? "-"}</td>
                <td>{client.children?.map(child => child.name).join(", ") || "-"}</td>
                <td>{client.profile?.concerns ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
